import React, { useEffect } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import { FaTimesCircle } from "react-icons/fa";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ToastContainer, toast } from "react-toastify";
import {
  editSponsor,
  getOnLoadSponsorImageService,
  getSponsorImage,
  getUploadedSponsorImage,
} from "../../../services/sponsors";
import { editSponsorStatus } from "../../../services/sponsors";
import "./Sponsors.css";
import IconButton from "@mui/material/IconButton";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Stack from "@mui/material/Stack";
import upload from "../../../assets/sponsors/upload.png";
import DialogBox from "../../../components/DialogBox";
import { useCallbackPrompt } from "../../../hooks/useCallbackPrompt";
import useSponsorDetails from "../../../hooks/Api/useSponsorsDetails";
import { BASE_API_URL } from "../../../utils/api-integration";
import {
  buttonLinkStyle,
  rowStyles,
} from "../StudySetup/Visits/Questionnaires/EditVisitQuestionnaire";
import { handleBackNavigationGeneralFun } from "../../../components/helpers/helper";

const AntSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#177ddc" : "#1890ff",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,.35)"
        : "rgba(0,0,0,.25)",
    boxSizing: "border-box",
  },
}));

export const allowedFormats = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

export const allowedExtensions = ["jpeg", "jpg", "png", "webp"];

const EditSponsors = () => {
  const navigate = useNavigate();
  const location = useLocation();

  var id = location?.state?.id;
  var name = location?.state?.name;
  var fileUrl = location?.state?.fileUrl;
  var isactive = location?.state?.isactive;

  const [load, setLoad] = React.useState(false);
  const [sponsorId, setSponsorId] = React.useState(id);
  const [sponsorName, setSponsorName] = React.useState(name);
  const [selectedFile, setSelectedFile] = React.useState(fileUrl);
  const [isActive, setIsActive] = React.useState(isactive);
  const [imgPreview, setImgPreview] = React.useState(null);

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required("Name is required")
      .max(255, "Name must be at most 255 characters")
      .matches(
        /^[A-Za-z][A-Za-z0-9\s!@#$%^&*()_+=-]*$/,
        "Name must start with a letter and cannot start with a number or special character"
      ),
    // file: yup.mixed().required("Image is required"),
  });

  const [showDialog, setShowDialog] = React.useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (name) {
      setValue("name", name, { shouldDirty: false });
    }
  }, [name, setValue]);

  const notify = () =>
    toast.success("Sponsor Updated Successfully", {
      // theme: "colored",
      toastId: "editSuccessToast",
    });

  const requestFailed = () =>
    toast.error("Something went wrong", {
      // theme: "colored",
      toastId: "requestFailed",
    });

  const activeSuccess = () =>
    toast.success("Status Updated Successfully", {
      // theme: "colored",
      toastId: "statusSuccessToast",
    });

  const imageIsRequired = () =>
    toast.warn("Image is required", {
      // theme: "colored",
      toastId: "imageRequired",
    });

  useEffect(() => {
    if (fileUrl) {
      getImageOnLoad(fileUrl);
    }
  }, [fileUrl]);

  const getImageOnLoad = async (fileUrl) => {
    try {
      const res = await getOnLoadSponsorImageService(fileUrl);
      if (res?.status === 200 && res?.data) {
        setImgPreview(res?.data);
      }
    } catch (err) {
      console.log("get image error: ", err);
    }
  };

  const isValidImage = (fileOrUrl) => {
    // Case 1: File object
    if (fileOrUrl instanceof File) {
      return allowedFormats.includes(fileOrUrl.type);
    }

    // Case 2: URL / string path
    if (typeof fileOrUrl === "string") {
      const extension = fileOrUrl.split(".").pop()?.toLowerCase();

      return allowedExtensions.includes(extension);
    }

    return false;
  };

  const onSubmit = async (data, e) => {
    e.preventDefault();

    try {
      const name = data.name;
      const fileUrl = selectedFile;
      if (!selectedFile) {
        imageIsRequired();
        return;
      }

      if (!isValidImage(selectedFile)) {
        toast.warn("Only JPEG, JPG, PNG, and WebP formats are allowed", {
          toastId: "invalidImageFormat",
        });
        return;
      }

      setLoad(true);
      setShowDialog(false);
      let imagePath = fileUrl;

      // Call upload API ONLY if binary file
      if (fileUrl instanceof File) {
        const imageRes = await getUploadedSponsorImage(fileUrl);

        if (imageRes?.status === 200 && imageRes?.data?.filePath) {
          imagePath = imageRes.data.filePath;
        } else {
          requestFailed();
          return;
        }
      }

      // Now always call editSponsor
      const res = await editSponsor(id, { name, imagePath });

      if (res.status) {
        notify();
        navigate("/all-sponsors");
      } else {
        requestFailed();
      }
    } catch (err) {
      console.log("submit error: ", err);
      requestFailed();
    } finally {
      setLoad(false);
    }
  };

  const cancelSponsor = (e) => {
    e.preventDefault();

    if (showDialog) {
      const confirmExit = window?.confirm(
        "You have unsaved changes, Are you sure you want to leave?"
      );

      if (confirmExit) {
        navigate("/all-sponsors");
      }
    } else {
      navigate("/all-sponsors");
    }
  };

  const handleEditName = (e) => {
    setSponsorName(e.target.value);
  };

  const removeImage = () => {
    setImgPreview(null);
    setSelectedFile(null);
  };

  const handleActiveChange = async (sponsorId, event) => {
    setLoad(true);
    const activeChecked = event.target.checked;
    setIsActive(activeChecked);
    setShowDialog(true);
    const res = await editSponsorStatus(sponsorId, activeChecked);
    if (res.status === 200) {
      setLoad(false);
      activeSuccess();
    } else {
      setLoad(false);
      requestFailed();
    }
  };

  const handleSponsorInput = (event) => {
    let name = event.target.value;
    if (name.length > 0 && name[0] === " ") {
      name = name.trimStart();
      event.target.value = name;
    }
    if (name) {
      setShowDialog(true);
      clearErrors("name");
    }
  };

  function replaceFirstSevenChars(originalString, replacementString) {
    // Ensure the replacement string is not longer than the original string
    if (replacementString?.length > originalString?.length) {
      throw new Error("Replacement string is longer than the original string");
    }

    // Replace the first seven characters
    const newString = replacementString + originalString?.slice(7);
    return newString;
  }

  return (
    <>
      {load ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "70vh",
          }}
        >
          <BeatLoader color="#3661eb" />
        </div>
      ) : (
        <div className="content-body">
          <DialogBox
            showDialog={showPrompt}
            confirmNavigation={confirmNavigation}
            cancelNavigation={cancelNavigation}
          />
          <p className="admin-link" style={{ fontWeight: "600" }}>
            <button
              style={buttonLinkStyle}
              onClick={(e) => {
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "homepage",
                  navigate
                );
              }}
            >
              Home
            </button>
            {" | "}
            <button
              style={buttonLinkStyle}
              onClick={(e) => {
                handleBackNavigationGeneralFun(
                  e,
                  showDialog,
                  "all-sponsors",
                  navigate
                );
              }}
            >
              Sponsors
            </button>
            {" | "}
            <span
              style={{
                color: "#4b8ac0",
                cursor: "pointer",
              }}
            >
              Update Sponsor
            </span>
          </p>
          <p className="sponsor-heading">Update Sponsor</p>
          <form
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          >
            <div className="uploadField">
              <label className="uploadInputLabel">
                Upload Image <span style={{ color: "red" }}>*</span>
              </label>
              <Row>
                <Col md={4} className="mt-3">
                  {imgPreview === null ? (
                    <>
                      <IconButton
                        color="primary"
                        aria-label="upload picture"
                        component="label"
                      >
                        <input
                          hidden={true}
                          accept="image/*"
                          name="file"
                          type="file"
                          {...register("file", { required: true })}
                          onChange={(event) => {
                            var file = event.target.files[0];
                            const reader = new FileReader();
                            var url = reader.readAsDataURL(file);
                            reader.onloadend = function (e) {
                              setImgPreview([reader.result]);
                            };
                            setSelectedFile(file);
                            setShowDialog(true);
                          }}
                        />
                        <img src={upload} className="uploadImg" />
                      </IconButton>
                      <span style={{ color: "red", marginLeft: "1%" }}>
                        {errors.file?.message}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="relative inline-block">
                        <button
                          type="button"
                          className="absolute top-1 right-1 bg-transparent border-none cursor-pointer z-10"
                          onClick={() => removeImage()}
                        >
                          <FaTimesCircle className="text-red-500 text-xl" />
                        </button>
                        <img
                          style={{
                            height: "200px",
                            objectFit: "contain",
                            width: "100%",
                          }}
                          src={imgPreview}
                          className="uploadImgAfter block max-w-full rounded-lg"
                          alt="Uploaded"
                        />
                      </div>
                      <span className="text-blue-600 ml-1">
                        {errors.file?.message}
                      </span>
                    </>
                  )}
                </Col>
              </Row>
            </div>
            <div className="uploadField">
              <label className="uploadInputLabel">
                Name{" "}
                <span
                  style={{
                    color: "red",
                  }}
                >
                  *
                </span>
              </label>
              <Row>
                <Col md={4}>
                  <input
                    className="nameSponsorField"
                    type="text"
                    // value={sponsorName}
                    // defaultValue={sponsorName}
                    {...register("name", { required: true })}
                    // onChange={handleSponsorInput}
                    onChange={(e) => {
                      setShowDialog(true);
                      clearErrors("name");
                    }}
                  />
                  <span style={{ color: "red", marginTop: "1%" }}>
                    {errors.name?.message}
                  </span>
                </Col>
              </Row>
              <Row style={{ marginTop: "2%", paddingLeft: "0.5%" }}>
                <Col md={3}>
                  {load ? (
                    <div
                      style={{
                        alignItems: "center",
                        height: "70vh",
                      }}
                    >
                      <BeatLoader color="#3661eb" />
                    </div>
                  ) : (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <AntSwitch
                        checked={isActive}
                        onChange={(event) =>
                          handleActiveChange(sponsorId, event)
                        }
                        inputProps={{ "aria-label": "ant design" }}
                      />
                      <p className="generalHead">Active</p>
                    </Stack>
                  )}
                </Col>
                <Col md={9}></Col>
              </Row>
              <Row style={rowStyles}>
                <Col md={8}></Col>
                <Col md={2}>
                  <div className="study-management-head-end">
                    <button
                      type="button"
                      onClick={(e) => {
                        cancelSponsor(e);
                      }}
                      className="study-management-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </Col>
                <Col md={2}>
                  <div className="study-management-head-end">
                    <button
                      type="submit"
                      className="study-management-create-btn-md"
                    >
                      Update
                    </button>
                  </div>
                </Col>
              </Row>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default EditSponsors;
